import { defaultTheme } from "react-admin";
import { alpha, darken, lighten } from "@mui/material/styles";

const theme = {
  ...defaultTheme,
  palette: {
    ...defaultTheme.palette,
    primary: {
      main: "#6A26C8",
      dark: "#4E2683",
      light: "#9c68e2",
    },
    secondary: {
      main: "#361B5C",
    },
    info: {
      main: "#00000081",
    },
    error: {
      main: "#9D1B53",
      primary: "#ea0e0eff",
    },
    success: {
      main: "#2e7d32",
      light: "#84ad86ff",
    },
    warning: {
      main: "#ff9800",
      light: "#fbf6dc",
    },
    white: {
      main: "#ffffff",
    },
    homeIcon: {
      main: "transparent",
    },
  },
  components: {
    ...defaultTheme.components,
    // Soft tinted filled inputs with a focus halo (modern UI look); SU keeps react-admin's filled default.
    MuiFilledInput: {
      defaultProps: {
        disableUnderline: true,
      },
      styleOverrides: {
        root: {
          borderRadius: 10,
          backgroundColor: "#EFECF8",
          border: "1px solid #DFDAED",
          transition: "background-color .18s, border-color .18s, box-shadow .18s",
          "&:hover:not(.Mui-disabled, .Mui-error, .Mui-focused)": {
            backgroundColor: "#E9E4F6",
          },
          "&.Mui-focused": {
            backgroundColor: "#fff",
            borderColor: "#6A26C8",
            boxShadow: `0 0 0 4px ${alpha("#6A26C8", 0.16)}`,
          },
          "&.Mui-error": {
            backgroundColor: "#FDF4F5",
            borderColor: "#9D1B53",
          },
          "&.Mui-focused.Mui-error": {
            boxShadow: `0 0 0 4px ${alpha("#9D1B53", 0.14)}`,
          },
          "&.Mui-disabled": {
            backgroundColor: "#F3F3F6",
            borderColor: "#E6E6EC",
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        shrink: {
          fontWeight: 500,
        },
      },
    },
    RaAppBar: {
      styleOverrides: {
        root:
          process.env.REACT_APP_TEST_ENVIRONMENT === "true"
            ? { color: "#fff", backgroundColor: "#9D1B53" }
            : { color: "#fff" },
      },
    },
    // RaConfigurable: {
    //   styleOverrides: {
    //     root: { color: "#fff" },
    //   },
    // },
    // RaLoadingIndicator: {
    //   styleOverrides: {
    //     root: { color: "#fff" },
    //   },
    // },
    RaSimpleFormIterator: {
      styleOverrides: {
        root: {
          "& .RaSimpleFormIterator-line": {
            borderLeft: "5px solid #4E2683",
            backgroundColor: "#F5F5F5",
            borderRadius: "5px",
            padding: "2rem",
            marginBottom: "2.5rem",
          },
          "&  .RaSimpleFormIterator-form ": {
            width: "100%",
            position: "relative",
          },
        },
      },
    },
    RaArrayInput: {
      styleOverrides: {
        root: {
          "& .RaArrayInput-label": {
            color: "#4E2683",
            fontWeight: 700,
            marginBottom: "7px",
            whiteSpace: "pre-wrap",
          },
        },
      },
    },
    RaLabeled: {
      styleOverrides: {
        root: {
          "& .RaLabeled-label": {
            color: "#6A26C8",
            fontWeight: 600,
            fontSize: 11,
          },
        },
      },
    },
    MuiTypography: {
      defaultProps: {
        sx: { fontSize: 14 },
      },
    },
    RaSidebar: {
      styleOverrides: {
        root: {
          "& .RaSidebar-fixed": {
            position: "fixed",
            height: "92vh",
            overflowY: "auto",
          },
        },
      },
    },
    RaMultiLevelMenu: {
      styleOverrides: {
        root: {
          backgroundColor: "#F6F9FE",
        },
      },
    },
    RaMenuItem: {
      styleOverrides: {
        root: {
          color: "#000",
          whiteSpace: "pre-wrap",
          "& .RaMenuItem-link": {
            whiteSpace: "pre-wrap",
          },
          "& .RaMenuItem-nestedList": {
            paddingLeft: "0px",
          },
          "& .RaMenuItem-itemButton.active": {
            whiteSpace: "pre-wrap",
            paddingLeft: "0.325rem",
            backgroundColor: "#fff",
            borderBottom: "1px solid #000",
          },
          "& .RaMenuItem-active": {
            color: "#000",
          },
          "& .RaMenuItem-menuIcon": {
            color: "#707070",
          },
        },
      },
    },
    MuiTable: {},
    MuiTableHead: {
      defaultProps: {
        sx: {
          backgroundColor: "black",
          color: "white",
        },
      },
    },
    RaDatagrid: {
      styleOverrides: {
        root: {
          backgroundColor: "#fff",
          border: "0.5px solid #d6d1d0a3",
          borderRadius: "5px",
          "& .RaDatagrid-headerCell": {
            backgroundColor: "#BEBEBE",
            // Vertical gradient around the header color: lighter at the top, darker at the bottom.
            backgroundImage: `linear-gradient(180deg, ${lighten("#BEBEBE", 0.2)} 0%, ${darken("#BEBEBE", 0.12)} 100%)`,
            color: "#000",
            fontWeight: 700,
            fontSize: 15,
            paddingRight: "5px",
            paddingLeft: "10px",
            textAlign: "center",
          },
          "& .MuiTableSortLabel-root.Mui-active": {
            color: "#fff",
          },
          "& .MuiTableSortLabel-root.Mui-active .MuiTableSortLabel-icon": {
            color: "#fff",
          },
        },
      },
    },
  },
  homeIconStyle: {
    color: "#fff",
  },
  logoutStyle: {
    flexDirection: "column",
    justifyContent: "center",
    "& .MuiSvgIcon-root": { color: "#FFFFFF", fontSize: "125%" },
    position: "relative",
  },
  RaCreateButton: {
    styleOverrides: {
      root: {
        backgroundColor: "#6A26C8",
        color: "#fff",
        padding: ".3rem 1rem",
        "&:hover": {
          backgroundColor: "#4E2683",
        },
      },
    },
  },
  RaSaveButton: {
    styleOverrides: {
      root: {
        backgroundColor: "#6A26C8",
        color: "#fff",
        padding: ".3rem 1rem",
        "&:hover": {
          backgroundColor: "#4E2683",
        },
      },
    },
  },
  RaEditButton: {
    styleOverrides: {
      root: {
        backgroundColor: "#6A26C8",
        color: "#fff",
        padding: ".3rem 1rem",
        "&:hover": {
          backgroundColor: "#4E2683",
        },
      },
    },
  },
  RaButton: {
    styleOverrides: {
      root: {
        backgroundColor: "#6A26C8",
        color: "#fff",
        padding: ".3rem 1rem",
        "&:hover": {
          backgroundColor: "#4E2683",
        },
      },
    },
  },
  RaBulkDeleteWithUndoButton: {
    styleOverrides: {
      root: {
        color: "#FFF",
        "&:hover": {
          backgroundColor: "#fff",
          color: "#4E2683",
        },
      },
    },
  },
};

export default theme;
